<template>
  <Layout>
    <div class="container" v-if="event">
      <div class="reports-top">
        <div class="title">
          Reports For {{ event.conference_name
          }}{{ selectedReport ? " - " + selectedReport.title : "" }}
        </div>
        <div class="actions" v-if="authenticationStore.hasPolicy('school.events.general.write')" >
          <RouterLink
            style="text-decoration: none"
            :to="{
              name: 'school-edit-events'
            }"
          >
            <div class="action">
              <v-icon class="icon" color="primary">mdi-calendar-edit</v-icon>
              <span class="text">Edit</span>
            </div>
          </RouterLink>
          <RouterLink
            style="text-decoration: none"
            :to="{
              name: 'school-edit-schedule'
            }"
          >
            <div class="action">
              <v-icon class="icon" color="primary">mdi-calendar-plus</v-icon>
              <span class="text">Schedule</span>
              <v-tooltip activator="parent" location="bottom">
                Add/remove teachers and teams from the event and view/edit their schedule
              </v-tooltip>
            </div>
          </RouterLink>
        </div>
      </div>
      <div style="max-width: 100%">
        <template v-if="selectedReport?.value === 'teacher'">
          <p class="text-primary mb-1 mt-2"><strong>Please select one or more teachers to generate the report:</strong></p>
          <div class="d-flex gap-3" style="flex-wrap: wrap">
            <div style="width: 250px; flex-shrink: 0; flex-grow: 0">
              <Autocomplete
                label="Teachers"
                bg-color="white"
                density="compact"
                :items="teachers"
                :loading="teachersS.teachers.loading"
                item-value="value"
                item-title="title"
                v-model="state.teacher"
                :multiple="true"
                clearable
                color="primary"
                :max-items="100"
                data-cy="report-teacher-select"
                :item-props="{
                  'data-cy': 'report-teacher-select-item'
                }"
                placeholder="Select teachers"
              >
              </Autocomplete>
            </div>
            <div style="width: 250px; flex-shrink: 0; flex-grow: 0">
              <Autocomplete
                label="Conference Day (optional)"
                bg-color="white"
                density="compact"
                :items="conferenceDays"
                item-value="value"
                item-title="title"
                v-model="state.conferenceDay"
                clearable
                color="primary"
                :max-items="100"
                data-cy="report-conference-day-select"
                :item-props="{
                  'data-cy': 'report-conference-day-select-item'
                }"
                placeholder="Select a conference day"
              >
              </Autocomplete>
            </div>
          </div>
        </template>
        <template v-if="selectedReport?.value === 'team'">
          <p class="text-primary mb-1 mt-2"><strong>Please select a team to generate the report:</strong></p>
          <div class="d-flex gap-3" style="flex-wrap: wrap">
            <div style="width: 250px; flex-shrink: 0; flex-grow: 0">
              <Autocomplete
                label="Teams"
                bg-color="white"
                density="compact"
                :items="teams"
                :loading="teamsS.teams.loading"
                item-value="value"
                item-title="title"
                v-model="state.team"
                clearable
                color="primary"
                :max-items="100"
                data-cy="report-team-select"
                :item-props="{
                  'data-cy': 'report-team-select-item'
                }"
                placeholder="Select a team"
              >
              </Autocomplete>
            </div>
            <div style="width: 250px; flex-shrink: 0; flex-grow: 0">
              <Autocomplete
                label="Conference Day (optional)"
                bg-color="white"
                density="compact"
                :items="conferenceDays"
                item-value="value"
                item-title="title"
                v-model="state.conferenceDay"
                clearable
                color="primary"
                :max-items="100"
                data-cy="report-conference-day-select"
                :item-props="{
                  'data-cy': 'report-conference-day-select-item'
                }"
                placeholder="Select a conference day"
              >
              </Autocomplete>
            </div>
          </div>
        </template>
        <template v-if="selectedReport?.value === 'parent'">
          <p class="text-primary mb-1 mt-2"><strong>Please select a parent to generate the report:</strong></p>
          <div class="d-flex gap-3" style="flex-wrap: wrap">
            <div style="width: 250px; flex-shrink: 0; flex-grow: 0">
              <Autocomplete
                label="Parents"
                bg-color="white"
                density="compact"
                :items="parents"
                :loading="parentsS.parents.loading"
                item-value="value"
                item-title="title"
                v-model="state.parent"
                clearable
                color="primary"
                :max-items="100"
                data-cy="report-parent-select"
                :item-props="{
                  'data-cy': 'report-parent-select-item'
                }"
                placeholder="Select a parent"
              >
              </Autocomplete>
            </div>
            <div style="width: 250px; flex-shrink: 0; flex-grow: 0">
              <Autocomplete
                label="Conference Day (optional)"
                bg-color="white"
                density="compact"
                :items="conferenceDays"
                item-value="value"
                item-title="title"
                v-model="state.conferenceDay"
                clearable
                color="primary"
                :max-items="100"
                data-cy="report-conference-day-select"
                :item-props="{
                  'data-cy': 'report-conference-day-select-item'
                }"
                placeholder="Select a conference day"
              >
              </Autocomplete>
            </div>
          </div>
        </template>
      </div>
      <!-- Debug info removed for production -->
      
      <!-- Message and Print button when multiple teachers selected (print only) -->
      <div v-if="selectedReport?.value === 'teacher' && Array.isArray(state.teacher) && state.teacher.length > 1" class="d-flex flex-column align-center justify-center" style="margin: 40px 0; padding: 20px; background-color: #f5f5f5; border-radius: 8px;">
        <div class="text-center mb-4">
          <v-icon color="primary" size="48" class="mb-2">mdi-printer</v-icon>
          <p class="text-primary mb-1" style="font-size: 1.1rem; font-weight: 600;">
            Multiple teachers selected
          </p>
          <p class="text-secondary" style="font-size: 0.9rem;">
            Table view is not available for multiple teachers. Use the "Print" button below to generate reports for the selected teachers.
          </p>
        </div>
        <v-btn
          @click="printSelectedTeachers"
          append-icon="mdi-printer"
          color="primary"
          :loading="printingSelected"
          size="large"
        >
          Print Selected Teachers
        </v-btn>
      </div>
      
      <!-- Signup Analytics Component (only for stats report) -->
      <SignupAnalytics v-if="selectedReport?.value === 'stats'" :event-id="eventId" />
      
      <ReportTable
        v-if="selectedReport"
        :show="showTable"
        :data="filteredTableData"
        :columns="customizedColumns"
        :loading="loading"
        :name="selectedReport!.title"
        :type="selectedReport.value"
        :event-id="eventId"
        :entity-name="getEntityName()"
        :show-columns-button="selectedReport && data.table_cols && data.table_cols.length > 0"
        :selected-conference-day-id="selectedDayId"
        @open-column-modal="showColumnModal = true"
      ></ReportTable>
      <div v-if="!selectedReport" class="d-flex flex-wrap justify-content-center" style="gap: 20px">
        <template v-for="report in reportTypes" :key="report.value">
          <SelectReportItem :title="report.title" :value="report.value"></SelectReportItem>
        </template>
      </div>
    </div>
    <div
      v-if="(!event && confStore.loading) || (!reportTypes.length && loadingReports)"
      style="width: 100%; margin-top: 200px"
    >
      <LoadingState text="Loading Reports..."></LoadingState>
    </div>
    <ColumnCustomizationModal
      v-if="data.table_cols && data.table_cols.length > 0"
      v-model="showColumnModal"
      :columns="customizedColumns"
      @update:columns="handleColumnsUpdate"
    ></ColumnCustomizationModal>
  </Layout>
</template>

<script setup lang="ts">
import { useConferenceStore } from "@/stores/teacher/conference";
import { cloneDeep } from "lodash";
import { watch, computed, reactive, ref, onMounted } from "vue";
import Layout from "./Layout.vue";
import LoadingState from "@/components/loadingState.vue";
import { asyncComputed } from "@/utils/asyncComputed";
import FetchService from "@/services/fetch.service";
import type { ReportPayload } from "@/components/school/reports/constants";
import ReportTable from "@/components/school/reports/reportTable.vue";
import SelectReportItem from "@/components/school/reports/selectReportItem.vue";
import router from "@/router";
import useTeachers from "@/stores/school/teachers";
import useTeams from "@/stores/school/teams";
import useParents from "@/stores/school/parents";
import Autocomplete from "@/components/autocomplete.vue";
import { sort } from "@/utils/sort";
import { addEmptyTimeSlot, printSelectedTable } from "@/components/school/reports/utils";
import ColumnCustomizationModal from "@/components/school/reports/ColumnCustomizationModal.vue";
import type { tableColumn } from "@/hooks/useTableSort";
import SignupAnalytics from "@/components/school/reports/SignupAnalytics.vue";
import dayjs from "dayjs";
import { useAlertsStore } from "@/stores/alerts";
import { useAuthenticationStore } from "@/stores/authentication";

const props = defineProps<{
  account?: string;
  eventId: string;
}>();

const state = reactive({
  type: (router.currentRoute.value.query.type as string) || "",
  teacher: [] as string[],
  team: undefined as string | undefined,
  parent: undefined as string | undefined,
  conferenceDay: undefined as string | undefined
});

const showColumnModal = ref(false);
const columnPreferences = reactive<Record<string, Record<string, boolean>>>({});
const printingSelected = ref(false);

const confStore = useConferenceStore();
const teachersS = useTeachers();
const teamsS = useTeams();
const parentsS = useParents();
const authenticationStore = useAuthenticationStore();
const teachers = computed(() =>
  teachersS.teachers.data
    .filter((i) => !i.deleted)
    .map((i) => ({ title: i.teacher_fname + " " + i.teacher_lname, value: i.uid }))
);
const teams = computed(() =>
  teamsS.teams.data.filter((i) => !i.deleted).map((i) => ({ title: i.team_name, value: i.uid }))
);
const parents = computed(() =>
  parentsS.parents.data
    .filter((i) => !i.deleted)
    .map((i) => ({ title: i.firstname + " " + i.lastname, value: i.uid }))
);

const event = computed(() => confStore.selected!);

// Not a conference_days_id. Vuetify drops an undefined item value and stores the title instead.
const ALL_DAYS = "all";

const conferenceDays = computed(() => {
  if (!event.value?.conference_days) return [];
  const days = event.value.conference_days.map((day) => ({
    title: `${dayjs.utc(day.start).format("MMM DD, YYYY")} - ${dayjs.utc(day.start).format("h:mm A")}`,
    value: day.conference_days_id
  }));
  return [{ title: "All Days", value: ALL_DAYS }, ...days];
});

const selectedDayId = computed(() => {
  const day = state.conferenceDay;
  if (day == null || day === ALL_DAYS) return undefined;
  return day;
});

const setEvent = async () => {
  await confStore.loadingPromise;
  const event = confStore.conferences.find((c) => c.conference_id === +props.eventId);
  if (event) {
    confStore.selected = cloneDeep(event);
    confStore.selected.conference_days = sort(confStore.selected.conference_days, (i) => i.start);
  }
};
setEvent();

const defVal = {
  table_data: [],
  table_cols: []
};

const { data, loading } = asyncComputed(
  async (controller) => {
    if (!event.value || !state.type) return defVal;

    // Handle entity-specific report types
    if (state.type === "teacher" && (!state.teacher || state.teacher.length === 0)) return defVal;
    if (state.type === "team" && !state.team) return defVal;
    if (state.type === "parent" && !state.parent) return defVal;
    
    // Validate report type exists in available report types
    if (!reportTypes.value.find(rt => rt.value === state.type)) {
      console.warn(`Report type '${state.type}' not found in available report types`);
      return defVal;
    }

    // Handle teacher reports with multiple selection
    if (state.type === "teacher" && Array.isArray(state.teacher)) {
      const teacherIds = state.teacher;
      
      // Single teacher: use existing pattern (table view enabled)
      if (teacherIds.length === 1) {
        const url = `/reports/teacher/${props.eventId}?teacher=${teacherIds[0]}`;
        console.log(`Fetching teacher report for ID:`, teacherIds[0]);
        const res = (await FetchService.fetchData(
          url,
          "get",
          undefined,
          undefined,
          controller
        )) as ReportPayload;
        
        if (res?.table_cols && res?.table_data) {
          // Apply addEmptyTimeSlot if needed
          if ("conference_days" in res) {
            try {
              if (Array.isArray(res.table_data) && 
                  Array.isArray(res.conference_days) && 
                  res.conference_days.length > 0) {
                res.table_data = addEmptyTimeSlot(res.table_data, res.conference_days as any[]);
              }
            } catch (error) {
              console.error("Error in addEmptyTimeSlot:", error);
            }
          }
          return res;
        }
        return defVal;
      }
      
      // Multiple teachers: return empty data (table viewing disabled, only printing available)
      if (teacherIds.length > 1) {
        return defVal;
      }
    }

    // Handle other report types (team, parent) - existing logic
    let url = `/reports/${state.type}/${props.eventId}`;
    if (["team", "parent"].includes(state.type as string)) {
      const entityId = state[state.type];
      url += `?${state.type}=${entityId}`;
      console.log(`Fetching ${state.type} report for ID:`, entityId);
    }
    const res = (await FetchService.fetchData(
      url,
      "get",
      undefined,
      undefined,
      controller
    )) as ReportPayload;
    if (res?.table_cols && res?.table_data) {
      console.log("API Response for report:", { 
        type: state.type,
        id: state[state.type],
        hasTableData: Array.isArray(res.table_data),
        tableDataLength: res.table_data?.length || 0,
        hasConferenceDays: "conference_days" in res,
        conferenceDaysLength: Array.isArray(res.conference_days) ? res.conference_days.length : 0,
        tableData: res.table_data,
        tableColumns: res.table_cols
      });
      
      if (["teacher", "team"].includes(state.type) && "conference_days" in res) {
        try {
          if (Array.isArray(res.table_data) && 
              Array.isArray(res.conference_days) && 
              res.conference_days.length > 0) {
            res.table_data = addEmptyTimeSlot(res.table_data, res.conference_days as any[]);
          } else {
            console.log("Skipping addEmptyTimeSlot due to missing or invalid data");
          }
        } catch (error) {
          console.error("Error in addEmptyTimeSlot:", error);
        }
      }
      return res;
    }
    return defVal;
  },
  [event, () => state.type, () => state.teacher, () => state.parent, () => state.team],
  undefined,
  undefined,
  defVal
);

const { data: reportTypes, loading: loadingReports } = asyncComputed(
  async () => {
    const res = await FetchService.fetchData("/reports", "get");
    if (Array.isArray(res))
      return res as {
        value: string;
        title: string;
      }[];
    return [];
  },
  undefined,
  undefined,
  undefined,
  []
);
const selectedReport = computed(() => reportTypes.value.find((i) => i.value === state.type));

const showTable = computed(() => {
  if (!selectedReport.value) return false;
  if (state.type === "teacher" && (!state.teacher || state.teacher.length === 0)) return false;
  // Only show table if exactly 1 teacher is selected (multiple teachers = print only)
  if (state.type === "teacher" && state.teacher.length > 1) return false;
  if (state.type === "team" && !state.team) return false;
  if (state.type === "parent" && !state.parent) return false;
  return true;
});
watch([() => props.eventId, () => confStore.conferences.length], setEvent);
watch([() => router.currentRoute.value.query.type, reportTypes], () => {
  if (!loadingReports.value) {
    if (reportTypes.value.find((r) => r.value === router.currentRoute.value.query.type))
      state.type = router.currentRoute.value.query.type as any;
    else state.type = "";
  }
});

const getEntityName = () => {
  const type = selectedReport.value?.value;
  if (type == "parent") {
    const p = parentsS.parents.data.find((p) => p.uid == state.parent);
    if (p) return `${p.firstname}, ${p.lastname}`;
    return;
  }
  if (type == "teacher") {
    if (Array.isArray(state.teacher)) {
      if (state.teacher.length === 0) return;
      if (state.teacher.length === 1) {
        const t = teachersS.teachers.data.find((t) => t.uid == state.teacher[0]);
        if (t) return `${t.teacher_fname}, ${t.teacher_lname}`;
        return;
      }
      // Multiple teachers: show count or list names
      const teacherNames = state.teacher
        .map(uid => {
          const t = teachersS.teachers.data.find((t) => t.uid == uid);
          return t ? `${t.teacher_fname} ${t.teacher_lname}` : null;
        })
        .filter(Boolean);
      
      if (teacherNames.length > 0) {
        // Show count if more than 3, otherwise show names
        if (teacherNames.length > 3) {
          return `${teacherNames.length} teachers`;
        }
        return teacherNames.join(", ");
      }
      return `${state.teacher.length} teachers`;
    }
    return;
  }
  if (type == "team") {
    const t = teamsS.teams.data.find((t) => t.uid == state.team);
    if (t) return `${t.team_name}`;
    return;
  }
  return;
};

// Get preference key for current report
const getPreferenceKey = () => {
  return state.type || "";
};

// Load column preferences from localStorage
const loadColumnPreferences = () => {
  try {
    const saved = localStorage.getItem(`reportColumns_${getPreferenceKey()}`);
    if (saved) {
      const prefs = JSON.parse(saved);
      Object.assign(columnPreferences, { [getPreferenceKey()]: prefs });
    }
  } catch (error) {
    console.warn("Failed to load column preferences:", error);
  }
};

// Save column preferences to localStorage
const saveColumnPreferences = () => {
  try {
    const key = getPreferenceKey();
    if (columnPreferences[key]) {
      localStorage.setItem(
        `reportColumns_${key}`,
        JSON.stringify(columnPreferences[key])
      );
    }
  } catch (error) {
    console.warn("Failed to save column preferences:", error);
  }
};

// Initialize display property for columns from API response and apply preferences
const initializeColumns = (columns: tableColumn[]) => {
  if (!Array.isArray(columns)) return columns;
  
  const key = getPreferenceKey();
  const prefs = columnPreferences[key] || {};
  
  return columns.map((col) => {
    // If preference exists, use it; otherwise use API value or default to true
    const display = prefs[col.key] !== undefined 
      ? prefs[col.key] 
      : (col.display ?? true);
    
    return {
      ...col,
      display,
    };
  });
};

// Computed property for customized columns
const customizedColumns = computed(() => {
  if (!data.value?.table_cols || !Array.isArray(data.value.table_cols)) {
    return [];
  }
  return initializeColumns(data.value.table_cols);
});

// Filter table data by selected conference day
const filteredTableData = computed(() => {
  if (!data.value?.table_data || !Array.isArray(data.value.table_data)) {
    return [];
  }
  
  // No day, cleared picker (null), or All Days: every row.
  if (!selectedDayId.value) {
    return data.value.table_data;
  }

  return data.value.table_data.filter((row) => row.conference_days_id === selectedDayId.value);
});

// Handle column updates from modal
const handleColumnsUpdate = (updatedColumns: tableColumn[]) => {
  const key = getPreferenceKey();
  if (!columnPreferences[key]) {
    columnPreferences[key] = {};
  }
  
  // Store preferences
  updatedColumns.forEach((col) => {
    columnPreferences[key][col.key] = col.display ?? true;
  });
  
  saveColumnPreferences();
};

// Load preferences when component mounts or report type changes
onMounted(() => {
  loadColumnPreferences();
});

watch(
  () => state.type,
  () => {
    loadColumnPreferences();
  }
);

// Print selected teachers
const printSelectedTeachers = async () => {
  if (!selectedReport.value || state.type !== "teacher" || !Array.isArray(state.teacher) || state.teacher.length === 0) {
    return;
  }

  printingSelected.value = true;
  
  try {
    // Fetch first teacher's report to get column structure
    const firstTeacherUrl = `/reports/teacher/${props.eventId}?teacher=${state.teacher[0]}`;
    const firstRes = (await FetchService.fetchData(
      firstTeacherUrl,
      "get"
    )) as ReportPayload;
    
    if (!firstRes?.table_cols) {
      useAlertsStore().addAlert("error", "Failed to retrieve report structure. Please try again.");
      return;
    }

    const printState = reactive({
      loading: false,
      fetchCount: 0,
      totalCount: 0,
      bufferValue: 0,
    });

    await printSelectedTable(
      customizedColumns.value.length > 0
        ? customizedColumns.value
        : initializeColumns(firstRes.table_cols),
      selectedReport.value.title,
      "teacher",
      props.eventId,
      state.teacher,
      (totalCount: number, inc: boolean) => {
        printState.totalCount = totalCount;
        if (inc) {
          printState.fetchCount++;
          const increaseBy =
            Math.ceil(((printState.fetchCount + 1) * 100) / printState.totalCount) -
            printState.bufferValue;
          const times = 7;
          const eachTime = increaseBy / times;
          for (let i = 0; i < times; i += 1) {
            setTimeout(() => {
              if (printState.bufferValue + eachTime < 100) printState.bufferValue += eachTime;
            }, i * 100);
          }
        }
      },
      selectedDayId.value
    );
  } catch (error) {
    console.error("Error printing selected teachers:", error);
    useAlertsStore().addAlert("error", "Failed to print reports. Please try again.");
  } finally {
    printingSelected.value = false;
  }
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.reports-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: 20px;
  .title {
    font-size: 1.25rem;
    color: gray;
    font-weight: 600;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    //   width: 100%;
    gap: 20px;
    flex-grow: 1;
    .action {
      color: $primary;
      display: flex;
      flex-direction: column;
      align-items: center;
      cursor: pointer;
      padding: 5px;
      border-radius: 4px;

      &:active {
        outline: 2px solid $primary-75;
      }
      gap: 5px;
      .icon {
        font-size: 1.5rem;
        font-weight: 600;
        flex-grow: 1;
      }
      .text {
        font-weight: 600;
        text-transform: uppercase;
      }
    }
  }
}
</style>
